import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IKeepInTouchJobConfiguration, KeepInTouchJobConfigurationScope } from '../../../../../models/KeepInTouchAgent';
import {
	invalidateKeepInTouchConfigurations,
	useDeleteKeepInTouchConfiguration,
	useRunKeepInTouchJob,
} from '../../../../../queries/KeepInTouchAgent';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { Button } from '../../../../components-v2/Button';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { JobProgress } from '../JobProgress';
import { styleSheet } from './styles';

interface ConfigurationCardProps {
	config: IKeepInTouchJobConfiguration;
	onEdit: (config: IKeepInTouchJobConfiguration) => void;
	onViewScores: (configId: string) => void;
}

export const ConfigurationCard: React.FC<ConfigurationCardProps> = ({ config, onEdit, onViewScores }) => {
	const userSession = useUserSession();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const [activeJobId, setActiveJobId] = React.useState<string | null>(null);

	// For user-scoped jobs, only the creator should be able to view scores or run
	const isUserScope = config.scope === KeepInTouchJobConfigurationScope.User;
	const isCreator = config.creator?.id === userSession.user?.id;
	const canViewAndRun = !isUserScope || isCreator;

	const deleteMutation = useDeleteKeepInTouchConfiguration({
		onSuccess: () => {
			invalidateKeepInTouchConfigurations();
			toast.success(`"${config.name}" deleted`);
		},
		onError: () => {
			toast.error('Failed to delete configuration');
		},
	});

	const runMutation = useRunKeepInTouchJob({
		onSuccess: data => {
			invalidateKeepInTouchConfigurations();
			setActiveJobId(data.id);
			toast.success(`"${config.name}" is now running`);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error?.systemMessage || 'Failed to run configuration');
		},
	});

	const handleDelete = (shouldDelete: boolean) => {
		setShowDeleteConfirmation(false);
		if (shouldDelete) {
			deleteMutation.mutate(config.id);
		}
	};

	const handleRunNow = () => {
		runMutation.mutate(config.id);
	};

	const handleJobComplete = React.useCallback(() => {
		setActiveJobId(null);
		invalidateKeepInTouchConfigurations();
	}, []);

	const lastRunText = config.lastRunDate
		? `Last run: ${new Date(config.lastRunDate).toLocaleDateString()}`
		: 'Never run';
	const isRunning = !!activeJobId || runMutation.isPending;

	return (
		<>
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.cardInfo)}>
					<h3 className={css(styleSheet.cardName)}>
						{config.name}{' '}
						<span className={css(styleSheet.scopeBadge)}>
							{config.scope === KeepInTouchJobConfigurationScope.Account ? 'Account' : 'User'}
						</span>
					</h3>
					<div className={css(styleSheet.cardMeta)}>
						<span>{lastRunText}</span>
					</div>
					<div className={css(styleSheet.cardMeta)}>
						<span>
							Created by:{' '}
							{config.creator
								? config.creator.fullName ||
									[config.creator.firstName, config.creator.lastName].filter(Boolean).join(' ') ||
									config.creator.email ||
									'Unknown'
								: 'Unknown'}
							{config.creationDate ? ` on ${new Date(config.creationDate).toLocaleDateString()}` : ''}
						</span>
					</div>
					{activeJobId ? <JobProgress jobId={activeJobId} configId={config.id} onComplete={handleJobComplete} /> : null}
				</div>
				<div className={css(styleSheet.cardActions)}>
					{canViewAndRun ? (
						<>
							<Button variant='primary' size='sm' onClick={() => onViewScores(config.id)}>
								View Contacts
							</Button>
							<Button
								variant='secondary'
								size='sm'
								onClick={handleRunNow}
								isLoading={runMutation.isPending}
								disabled={isRunning}
							>
								Run Now
							</Button>
						</>
					) : null}
					<Button variant='secondary' size='sm' onClick={() => onEdit(config)}>
						Edit
					</Button>
					<Button variant='destructive-secondary' size='sm' onClick={() => setShowDeleteConfirmation(true)}>
						Delete
					</Button>
				</div>
			</div>
			<DeleteConfirmation
				isOpen={showDeleteConfirmation}
				onFinish={handleDelete}
				title='Delete Configuration'
				bodyText={`Are you sure you want to delete "${config.name}"? This will also delete all associated scores.`}
				showSpinner={deleteMutation.isPending}
			/>
		</>
	);
};
